import {
  GetCommunityBlockedUsersQuery,
  GetCommunityReportedContentQuery,
} from 'generated/graphql';
import { formatPostDate } from '../lib/formatPostDate';
import { CommunityId } from './community.types';
import { toAuthorId, toPostId } from './post.types';
import {
  BlockedCommunityUser,
  ModerationFeed,
  postKey,
  reasonLabel,
  ReportedPost,
  userKey,
} from './moderation.types';

type ReportedRow = NonNullable<
  GetCommunityReportedContentQuery['reportedContent']
>[number];
type BlockedRow = NonNullable<
  GetCommunityBlockedUsersQuery['blockedUsers']
>[number];

const formatBlockedAt = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

export const toReportedPosts = (
  rows: readonly ReportedRow[],
  communityId: CommunityId,
  now: Date = new Date(),
): ReportedPost[] => {
  const byActivity = new Map<string, ReportedPost>();

  for (const row of rows) {
    const label = reasonLabel(row.reason);
    const existing = byActivity.get(row.activityId);

    if (existing) {
      existing.reportCount += 1;
      existing.reportIds.push(row.id);
      if (label && !existing.reasons.includes(label)) {
        existing.reasons.push(label);
      }
      continue;
    }

    byActivity.set(row.activityId, {
      activityId: row.activityId,
      communityId,
      isReply: row.activityType === 'reply',
      parentPostId: row.parentId ? toPostId(row.parentId) : null,
      authorId: toAuthorId(row.author.id),
      authorName: row.author.username,
      authorAvatar: row.author.profilePicture,
      text: row.text,
      timeAgo: formatPostDate(new Date(row.createdAt), now),
      reportCount: 1,
      reasons: label ? [label] : [],
      reportIds: [row.id],
    });
  }

  return [...byActivity.values()];
};

export const withoutReportedPost = (
  feed: ModerationFeed,
  post: ReportedPost,
): ModerationFeed => ({
  ...feed,
  reported: feed.reported.filter((item) => postKey(item) !== postKey(post)),
});

export const withoutBlockedUser = (
  feed: ModerationFeed,
  user: BlockedCommunityUser,
): ModerationFeed => ({
  ...feed,
  blocked: feed.blocked.filter((item) => userKey(item) !== userKey(user)),
});

export const toBlockedUsers = (
  rows: readonly BlockedRow[],
  communityId: CommunityId,
): BlockedCommunityUser[] =>
  rows.map((row) => ({
    userId: toAuthorId(row.id),
    communityId,
    name: row.name ?? 'Unknown',
    avatar: row.mediaThumbnail,
    blockedAtLabel: formatBlockedAt(new Date(row.blockedAt)),
    itemsRemovedCount: row.itemsRemovedCount,
  }));
